import { type PanelManager } from '@databases/panel-types';
import { createEffect } from '@databases/selections';
import {
  type SelectionsApiContract,
  type DatabaseAdapter
} from '@databases/selections-api-types';

import { hideActionBar, showActionBar } from './action-bar-portal-state';
import { buildActionBarSelectionsData } from './utils/build-action-bar-selections-data';
import { buildActionsForSelectionMode } from './utils/build-actions-for-selection-mode';

export interface ActionBarEffectDeps {
  selectionsApi: SelectionsApiContract;
  contentId: string;
  databaseAdapter: DatabaseAdapter;
  panelManager: PanelManager;
  isTableLayout: () => boolean;
}

/**
 * Selection machine effect that shows/hides the action bar based on selection state.
 * This effect:
 * - Listens to selection deltas
 * - Queries SelectionsApi for selection state
 * - Queries DatabaseAdapter for business rules
 * - Passes data to ActionBarPortal (which uses hooks to complete the props)
 *
 * Only runs for TABLE layout — board and gallery do not support the action bar.
 */
export const createActionBarEffect = createEffect(
  ({
    selectionsApi,
    contentId,
    databaseAdapter,
    panelManager,
    isTableLayout
  }: ActionBarEffectDeps) => {
    return {
      dispose() {
        hideActionBar();
      },

      deltaTypes: ['selectionModified', 'selectionReplaced'],

      onDelta(_, machineState) {
        // if we're idle we don't show this
        if (machineState.mode === 'idle' || !isTableLayout()) {
          hideActionBar();

          return;
        }

        const selection = selectionsApi.getResolvedSelection();

        // if we're idle (redundant type check) OR
        // we're in cell with a single selections, we hide and exit
        if (
          selection.mode === 'idle' ||
          (selection.mode === 'cell' && selection.values.length === 1) ||
          (selection.mode === 'column' && selection.fields.length === 1)
        ) {
          hideActionBar();

          return;
        }

        // Build all action bar data (selection + business rules)
        const actionBarData = buildActionBarSelectionsData(
          selectionsApi,
          selection,
          databaseAdapter
        );

        const actions = buildActionsForSelectionMode(selection.mode, {
          contentId,
          selectionsApi,
          databaseAdapter,
          spaceKey: databaseAdapter.getSpaceKey(),
          panelManager
        });

        showActionBar({
          ...actionBarData,
          actions,
          contentId,
          panelManager,
          selectionsApi
        });
      }
    };
  }
);
